import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import RaceManageDialog from "./RaceManageDialog";
import { Crown, Sailboat, Trophy, Sparkle, Lock, LinkedinLogo, TwitterLogo, Copy, ShareNetwork, MedalMilitary, EyeSlash, Eye, Gear } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function RaceToCaribbean() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [filter, setFilter] = useState("all"); // all | tier1 | tier2 | qualified
  const [copied, setCopied] = useState(false);

  const loadRace = useCallback(() => {
    return axios.get(`${BACKEND}/api/race`).then((r) => setData(r.data));
  }, []);

  const loadHidden = useCallback(() => {
    if (!isAdmin) { setHidden([]); return Promise.resolve(); }
    return api.get("/race/hidden").then(({ data: d }) => setHidden(d.rows || []))
      .catch(() => setHidden([]));
  }, [isAdmin]);

  useEffect(() => {
    loadRace();
    loadHidden();
    const id = setInterval(loadRace, 60000);
    return () => clearInterval(id);
  }, [loadRace, loadHidden]);

  const hideAgent = async (row) => {
    if (!window.confirm(`Hide ${row.agent_name} from the public race board for this year?`)) return;
    try {
      await api.post("/race/hide", { agent_key: row.agent_id });
      toast.success(`${row.agent_name} hidden`);
      await Promise.all([loadRace(), loadHidden()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not hide agent");
    }
  };

  const unhideAgent = async (h) => {
    try {
      await api.post("/race/unhide", { agent_key: h.agent_key });
      toast.success(`${h.agent_name} restored`);
      await Promise.all([loadRace(), loadHidden()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not restore agent");
    }
  };

  // Public share URL — points at /api/share/race which carries OG meta tags
  // and 302-redirects browsers back to /race. Use a clean origin so the link
  // looks right when pasted into LinkedIn / iMessage / Slack etc.
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/api/share/race` : "";
  const shareText = "Watch CFN agents pace toward the Leader's Conference trip qualifier — live public leaderboard.";

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      // Clipboard API can fail under permission policy — silently ignore;
      // the URL is also displayed on screen so the user can copy it manually.
      console.warn("Clipboard copy failed:", e?.message || e);
    }
  };

  const nativeShare = async () => {
    if (!navigator.share) { copyShare(); return; }
    try {
      await navigator.share({ title: "Leader's Conference — CFN Trip Qualifier", text: shareText, url: shareUrl });
    } catch (e) {
      if (e?.name !== "AbortError") copyShare();
    }
  };

  const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0c4a6e] text-white flex items-center justify-center">
        <div className="font-display font-black text-3xl tracking-tighter">Loading…</div>
      </div>
    );
  }

  const rows = data.rows.filter((r) => {
    if (filter === "all") return true;
    if (filter === "qualified") return r.qualified;
    return r.tier === filter;
  });
  const qualifiedCount = data.rows.filter((r) => r.qualified).length;
  const topPace = data.rows[0]?.progress_pct || 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0c4a6e] via-[#0369a1] to-[#0284c7] text-white">
      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 pt-12 pb-6">
        <div className="flex items-center gap-3 text-cyan-200">
          <Sailboat weight="fill" size={28} />
          <div className="text-xs font-bold uppercase tracking-[0.3em]">CFN {data.year} Trip Qualifier</div>
        </div>
        <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter mt-2 leading-[0.9]">
          Leader&apos;s<br />Conference.
        </h1>
        <p className="text-cyan-100 mt-4 max-w-2xl text-lg">
          Real-time pacing toward the CFN Cruise to the Caribbean trip qualifier. Tier 1 (years 1-2): $100k paid Life + 20 cases. Tier 2 (3+ years): $300k paid GDC. Public board.
        </p>
        <div className="mt-6 flex flex-wrap gap-3" data-testid="race-stats">
          <div className="border-2 border-white/40 bg-white/10 backdrop-blur px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">Agents Racing</div>
            <div className="font-display font-black text-2xl">{data.rows.length}</div>
          </div>
          <div className="border-2 border-[#facc15] bg-[#facc15]/20 px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#fef9c3]">Qualified</div>
            <div className="font-display font-black text-2xl">{qualifiedCount}</div>
          </div>
          <div className="border-2 border-white/40 bg-white/10 backdrop-blur px-4 py-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-cyan-200">Top Pace</div>
            <div className="font-display font-black text-2xl">{topPace}%</div>
          </div>
          {isAdmin && (
            <RaceManageDialog
              year={data.year}
              onChanged={() => { loadRace(); loadHidden(); }}
              trigger={
                <button
                  type="button"
                  className="border-2 border-white bg-white text-[#09090b] px-4 py-2 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-xs hover:bg-[#facc15]"
                  data-testid="race-manage-btn"
                >
                  <Gear weight="bold" size={14} /> Manage Board
                </button>
              }
            />
          )}
        </div>

        {/* Share kit */}
        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="race-share">
          <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-200 mr-1">Share:</span>
          <a
            href={linkedInUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border-2 border-white bg-white text-[#0a66c2] px-3 py-1.5 font-bold uppercase text-xs tracking-widest hover:-translate-y-0.5 transition-transform"
            data-testid="share-linkedin"
          >
            <LinkedinLogo size={14} weight="fill" /> LinkedIn
          </a>
          <a
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 border-2 border-white bg-white text-black px-3 py-1.5 font-bold uppercase text-xs tracking-widest hover:-translate-y-0.5 transition-transform"
            data-testid="share-twitter"
          >
            <TwitterLogo size={14} weight="fill" /> X / Twitter
          </a>
          <button
            onClick={copyShare}
            className="inline-flex items-center gap-1.5 border-2 border-white bg-transparent text-white px-3 py-1.5 font-bold uppercase text-xs tracking-widest hover:bg-white/10"
            data-testid="share-copy"
          >
            <Copy size={14} weight="bold" /> {copied ? "Copied!" : "Copy Link"}
          </button>
          <button
            onClick={nativeShare}
            className="inline-flex items-center gap-1.5 border-2 border-white bg-transparent text-white px-3 py-1.5 font-bold uppercase text-xs tracking-widest hover:bg-white/10"
            data-testid="share-native"
          >
            <ShareNetwork size={14} weight="bold" /> More
          </button>
        </div>
      </div>

      {/* Goal Achievers Wall */}
      {data.goal_achievers?.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 pt-8" data-testid="goal-achievers-wall">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <MedalMilitary weight="fill" size={28} className="text-[#facc15]" />
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-cyan-200">Goal Achievers</div>
                <div className="font-display font-black text-3xl tracking-tighter">Personal Goal Wall · {data.year}</div>
              </div>
            </div>
            <div className="border-2 border-[#facc15] bg-[#facc15]/20 px-3 py-1.5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#fef9c3]">Hit Their Goal</div>
              <div className="font-display font-black text-xl text-white">{data.goal_achievers.length}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.goal_achievers.map((a, i) => (
              <motion.div
                key={a.agent_id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                className="border-2 border-[#facc15] bg-white text-[#09090b] p-4 relative overflow-hidden"
                data-testid={`achiever-${a.agent_id}`}
              >
                <div className="absolute -top-3 -right-3 opacity-10">
                  <MedalMilitary size={84} weight="fill" />
                </div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#0c4a6e]">
                  <Sparkle weight="fill" size={12} className="text-[#facc15]" /> Goal Hit
                </div>
                <div className="font-display font-black text-xl tracking-tighter mt-1">{a.agent_name}</div>
                <div className="mt-3 flex items-end justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Set</div>
                    <div className="font-bold">{fmt$(a.goal)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Posted</div>
                    <div className="font-bold text-[#22c55e]">{fmt$(a.ytd)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">At</div>
                    <div className="font-display font-black text-xl text-[#22c55e]">{a.over_pct}%</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <div className="inline-flex border-2 border-white bg-white/5 backdrop-blur p-1" data-testid="race-filters">
          {[
            { key: "all", label: "All" },
            { key: "tier1", label: "Tier 1" },
            { key: "tier2", label: "Tier 2" },
            { key: "qualified", label: "Qualified" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${filter === f.key ? "bg-[#facc15] text-[#0c4a6e]" : "text-white hover:bg-white/10"}`}
              data-testid={`race-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Podium */}
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {rows.slice(0, 3).map((r, i) => {
            const colors = ["#facc15", "#e5e7eb", "#fb923c"];
            const labels = ["GOLD", "SILVER", "BRONZE"];
            return (
              <motion.div
                key={r.agent_id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                className="border-2 border-white bg-white/10 backdrop-blur p-5 relative overflow-hidden"
                data-testid={`race-podium-${i + 1}`}
              >
                <div className="absolute -top-4 -right-4 opacity-20">
                  {i === 0 && <Crown size={120} weight="fill" />}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: colors[i] }}>{labels[i]}</div>
                <div className="font-display font-black text-3xl tracking-tighter mt-1">
                  {r.agent_name}
                  {r.is_external && <span className="ml-2 inline-block align-middle border-2 border-white px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-white/15">EXT</span>}
                </div>
                <div className="text-xs text-cyan-200 mt-1 uppercase tracking-widest">{r.tier === "tier1" ? "Tier 1 · Yrs 1-2" : "Tier 2 · Yrs 3+"}</div>
                <div className="mt-4 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-cyan-200">Pace</div>
                    <div className="font-display font-black text-4xl tracking-tighter" style={{ color: r.qualified ? "#22c55e" : colors[i] }}>{r.progress_pct}%</div>
                  </div>
                  {r.qualified && (
                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-[#22c55e] text-white border-2 border-white text-[10px] font-black uppercase tracking-widest">
                      <Sparkle weight="fill" size={10} /> Qualified
                    </div>
                  )}
                </div>
                <div className="mt-4 h-3 border-2 border-white bg-white/20 overflow-hidden">
                  <div className="h-full" style={{ width: `${r.progress_pct}%`, background: r.qualified ? "#22c55e" : colors[i] }} />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Full table */}
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        <div className="border-2 border-white bg-white text-[#09090b] overflow-x-auto">
          <table className="w-full border-collapse" data-testid="race-table">
            <thead>
              <tr className="bg-[#09090b] text-white">
                <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">#</th>
                <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Agent</th>
                <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Tier</th>
                <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest" title="Total paid Life / Disability / LTC premium — Tier 1 qualifier ($100k)">L / D / LTC Prem.</th>
                <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest" title="Total paid GDC — Tier 2 qualifier ($300k)">Total GDC</th>
                <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Pace</th>
                <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Progress</th>
                <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Status</th>
                {isAdmin && <th className="px-3 py-3" aria-label="Admin actions"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.agent_id} className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]" data-testid={`race-row-${r.agent_id}`}>
                  <td className="px-3 py-3 font-display font-black">{r.rank}</td>
                  <td className="px-3 py-3 font-bold">
                    {r.agent_name}
                    {r.is_external && <span className="ml-2 inline-block border-2 border-black px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#fef3c7]" title="External (non-user) agent">EXT</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] font-bold uppercase tracking-widest border-2 border-black px-1.5 py-0.5 ${r.tier === "tier1" ? "bg-[#bae6fd]" : "bg-[#fed7aa]"}`}>
                      {r.tier === "tier1" ? "T1 · 1-2 yrs" : "T2 · 3+ yrs"}
                    </span>
                  </td>
                  <td
                    className={`px-3 py-3 text-right font-display font-black text-lg tracking-tight ${r.tier === "tier1" ? "" : "text-zinc-400"}`}
                    data-testid={`race-ldltc-${r.agent_id}`}
                    title={r.tier === "tier1" ? "Tier 1 qualifier · goal $100,000" : "Shown for reference — Tier 2 qualifies on GDC"}
                  >
                    {fmt$(r.life_dis_ltc_premium || 0)}
                  </td>
                  <td
                    className={`px-3 py-3 text-right font-display font-black text-lg tracking-tight ${r.tier === "tier2" ? "" : "text-zinc-400"}`}
                    data-testid={`race-gdc-${r.agent_id}`}
                    title={r.tier === "tier2" ? "Tier 2 qualifier · goal $300,000" : "Shown for reference — Tier 1 qualifies on L/D/LTC + cases"}
                  >
                    {fmt$(r.total_gdc || 0)}
                  </td>
                  <td className="px-3 py-3 text-right font-display font-black">{r.progress_pct}%</td>
                  <td className="px-3 py-3 text-right text-xs text-zinc-600">
                    {r.tier === "tier1" ? (
                      <>{fmt$(r.details.life_premium)} / {fmt$(r.details.life_premium_target)} · {r.details.cases_paid}/{r.details.cases_target} cases</>
                    ) : (
                      <>{fmt$(r.details.gdc)} / {fmt$(r.details.gdc_target)} GDC</>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {r.qualified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#22c55e] text-white border-2 border-black text-[10px] font-black uppercase tracking-widest">
                        <Trophy weight="fill" size={10} /> Qualified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 border-2 border-black text-[10px] font-black uppercase tracking-widest text-zinc-500">
                        <Lock weight="bold" size={10} /> Pacing
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => hideAgent(r)}
                        className="border-2 border-black p-1.5 hover:bg-[#fb7185] hover:text-white"
                        title={`Hide ${r.agent_name} from the public race for ${data.year}`}
                        data-testid={`race-hide-${r.agent_id}`}
                      >
                        <EyeSlash size={12} weight="bold" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="px-4 py-10 text-center text-zinc-500">No agents on this board yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Admin-only: hidden agents footer */}
        {isAdmin && hidden.length > 0 && (
          <div className="mt-4 border-2 border-white bg-white text-[#09090b]" data-testid="race-hidden-section">
            <button
              type="button"
              onClick={() => setShowHidden((v) => !v)}
              className="w-full text-left px-4 py-3 flex items-center justify-between font-bold uppercase tracking-widest text-xs hover:bg-zinc-100"
              data-testid="race-hidden-toggle"
            >
              <span className="inline-flex items-center gap-2">
                <EyeSlash size={14} weight="bold" />
                {hidden.length} hidden {hidden.length === 1 ? "agent" : "agents"} · click to {showHidden ? "collapse" : "view"}
              </span>
              <span className="text-[10px] text-zinc-500">Admin only · {data.year}</span>
            </button>
            {showHidden && (
              <ul className="border-t-2 border-black divide-y-2 divide-black">
                {hidden.map((h) => (
                  <li key={h.agent_key} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`race-hidden-row-${h.agent_key}`}>
                    <div>
                      <span className="font-bold">{h.agent_name}</span>
                      {h.is_external && <span className="ml-2 inline-block border-2 border-black px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#fef3c7]">EXT</span>}
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                        Hidden {h.hidden_at ? new Date(h.hidden_at).toLocaleDateString() : ""}
                        {h.hidden_by_name ? ` by ${h.hidden_by_name}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={() => unhideAgent(h)}
                      className="inline-flex items-center gap-1 border-2 border-black px-2 py-1 hover:bg-[#22c55e] hover:text-white text-[11px] font-bold uppercase tracking-widest"
                      data-testid={`race-unhide-${h.agent_key}`}
                    >
                      <Eye size={12} weight="bold" /> Restore
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        <div className="mt-6 text-center text-cyan-200 text-xs uppercase tracking-widest">
          As of {new Date(data.as_of).toLocaleString()} · Refreshes every 60s
        </div>
      </div>
    </div>
  );
}
